import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (password) => api.post("/auth/login", { password });
export const verifyAuth = () => api.get("/auth/verify");

// Teams
export const getTeams = () => api.get("/teams");
export const getTeam = (id) => api.get(`/teams/${id}`);
export const createTeam = (data) => api.post("/teams", data);
export const updateTeam = (id, data) => api.put(`/teams/${id}`, data);
export const deleteTeam = (id) => api.delete(`/teams/${id}`);

// Players
export const getPlayers = (teamId) => api.get("/players", { params: { team_id: teamId } });
export const getPlayer = (id) => api.get(`/players/${id}`);
export const createPlayer = (data) => api.post("/players", data);
export const createPlayersBulk = (data) => api.post("/players/bulk", data);
export const updatePlayer = (id, data) => api.put(`/players/${id}`, data);
export const deletePlayer = (id) => api.delete(`/players/${id}`);

// Matches
export const getMatches = (params) => api.get("/matches", { params });
export const getMatch = (id) => api.get(`/matches/${id}`);
export const createMatch = (data) => api.post("/matches", data);
export const updateMatch = (id, data) => api.put(`/matches/${id}`, data);
export const deleteMatch = (id) => api.delete(`/matches/${id}`);

// Standings
export const getStandings = () => api.get("/standings");

// Knockout
export const getKnockout = () => api.get("/knockout");
export const createSemifinal = (num) => api.post(`/knockout/create-semifinal?semifinal_num=${num}`);
export const createFinal = () => api.post("/knockout/create-final");

// Stats
export const getPlayerStats = () => api.get("/stats/players");
export const getTeamStats = () => api.get("/stats/teams");
export const getMvpLeaderboard = () => api.get("/stats/mvp");

// Referee/Delegate functions (players who can officiate)
export const togglePlayerReferee = (playerId) => api.patch(`/players/${playerId}/toggle-referee`);
export const togglePlayerDelegate = (playerId) => api.patch(`/players/${playerId}/toggle-delegate`);
export const getAvailableOfficials = () => api.get(`/players/officials/available`);
export const assignRefereesToMatch = (matchId, assignments, sendNotifications = true) => 
  api.post(`/matches/${matchId}/assign-referees?send_notifications=${sendNotifications}`, assignments);

// Messages
export const getMessages = () => api.get("/messages");
export const sendMessage = (data) => api.post("/messages", data);
export const markMessageRead = (id) => api.put(`/messages/${id}/read`);
export const deleteMessage = (id) => api.delete(`/messages/${id}`);

// Settings
export const getSettings = () => api.get("/settings");
export const updateSettings = (params) => api.put("/settings", null, { params });

// Blog
export const getBlogPosts = () => api.get("/blog");
export const getBlogPost = (id) => api.get(`/blog/${id}`);
export const createBlogPost = (data) => api.post("/blog", data);
export const updateBlogPost = (id, data) => api.put(`/blog/${id}`, data);
export const deleteBlogPost = (id) => api.delete(`/blog/${id}`);

// Blog Comments (no auth required for reading/posting)
export const getBlogComments = (postId) => axios.get(`${API_URL}/api/blog/${postId}/comments`);
export const createBlogComment = (postId, data) => axios.post(`${API_URL}/api/blog/${postId}/comments`, data);
export const deleteBlogComment = (postId, commentId) => api.delete(`/blog/${postId}/comments/${commentId}`);

// Sponsors
export const getSponsors = () => api.get("/sponsors");
export const getSponsor = (id) => api.get(`/sponsors/${id}`);
export const getSponsorByTeam = (teamId) => api.get(`/sponsors/team/${teamId}`);
export const createSponsor = (data) => api.post("/sponsors", data);
export const updateSponsor = (id, data) => api.put(`/sponsors/${id}`, data);
export const deleteSponsor = (id) => api.delete(`/sponsors/${id}`);

// Analytics
export const recordVisit = (data) => axios.post(`${API_URL}/api/analytics/visit`, data);
export const getAnalyticsSummary = () => api.get("/analytics/summary");

// Public API (no auth)
export const getPublicData = (token) => axios.get(`${API_URL}/api/public/${token}`);

export default api;
